import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import Loading from '../../../components/loading';
import ProcedureCatalogFormModal from '../procedureCatalogFormModal';
import { useProceduresCatalog } from '../../../hooks/useProceduresCatalog';
import { useDeleteProcedureCatalog } from '../hooks/useDeleteProcedureCatalog';

const formatCurrency = (value) =>
  value != null ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';

function ProcedureCatalogListWeb() {
  const navigate = useNavigate();

  const {
    proceduresCatalog: procedures,
    isLoading,
    error: listError,
    refetch: getProcedures,
  } = useProceduresCatalog();
  const { remove } = useDeleteProcedureCatalog();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProcedureId, setSelectedProcedureId] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const clickMenu = (event, procedureId) => {
    setAnchorEl(event.currentTarget);
    setSelectedProcedureId(procedureId);
  };

  const closeMenu = () => setAnchorEl(null);

  const openCreateModal = () => {
    setSelectedProcedure(null);
    setModalOpen(true);
  };

  const openEditModal = () => {
    const proc = procedures.find((p) => p.id === selectedProcedureId);
    if (proc) {
      setSelectedProcedure(proc);
      setModalOpen(true);
    }
    closeMenu();
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedProcedure(null);
  };

  const openConfirmModal = () => {
    setAnchorEl(null);
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedProcedureId(null);
  };

  const handleProcedureSaved = () => {
    getProcedures();
    setSuccessMessage('Procedimento salvo com sucesso.');
    setShowSuccessMessage(true);
  };

  const confirmDelete = () => {
    setConfirmModalOpen(false);
    remove(selectedProcedureId)
      .then((res) => {
        getProcedures();
        setSuccessMessage(res.data ?? 'Procedimento excluído com sucesso.');
        setShowSuccessMessage(true);
      })
      .catch((err) => {
        setShowErrorMessage(true);
        setErrorMessage(err.response?.data ?? 'Erro ao excluir procedimento.');
        if (err.response?.status === 401) navigate('/expired-session');
      })
      .finally(() => {
        setSelectedProcedureId(null);
      });
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ p: 4 }}>
        <Container maxWidth="lg">
          <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: '12px' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h5" component="h1" sx={{ fontWeight: '600' }}>
                Procedimentos
              </Typography>
              <Button variant="outlined" onClick={openCreateModal} data-cy="add-catalog-btn">
                Adicionar Procedimento
              </Button>
            </Box>

            <List>
              {procedures.length === 0 && !isLoading ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  Nenhum procedimento cadastrado
                </Typography>
              ) : (
                procedures.map((procedure) => (
                  <React.Fragment key={procedure.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton onClick={(event) => clickMenu(event, procedure.id)}>
                          <MoreVertIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={procedure.description}
                        secondary={formatCurrency(procedure.amount)}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>
        </Container>

        <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu}>
          <MenuItem onClick={openEditModal}>Editar</MenuItem>
          <MenuItem onClick={openConfirmModal} sx={{ color: 'error.main' }}>
            Excluir
          </MenuItem>
        </Menu>

        <ProcedureCatalogFormModal
          open={isModalOpen}
          onClose={closeModal}
          onSave={handleProcedureSaved}
          procedure={selectedProcedure}
        />

        <Dialog open={isConfirmModalOpen} onClose={closeConfirmModal}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Você tem certeza que deseja excluir este procedimento? Essa ação não pode ser
              desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeConfirmModal}>Cancelar</Button>
            <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default ProcedureCatalogListWeb;
